/* GASSISA
=========================================================================================
* Descripcion:
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 15/07/2026 | Lic. Jose Luis Aragon Cervantes  | Administracion de solicitudes de dispositivos moviles
* ---------------------------------------------------------------------------------------
*/

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { useSesion } from "../../context/System/SesionContext";
import {
  apiTenantMobileDeviceAccessApprove,
  apiTenantMobileDeviceAccessList,
  apiTenantMobileDeviceAccessRevoke,
  apiTenantMobileDeviceAccessDelete,
} from "../../settings/ApiConfig";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";

const statusLabels = {
  PENDING_APPROVAL: { label: "Pendiente", color: "warning" },
  APPROVED: { label: "Aprobado", color: "success" },
  REVOKED: { label: "Revocado", color: "default" },
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

export default function FrmAdministracionDispositivos() {
  const navigate = useNavigate();
  const { tenantAdminDevice, tenantAdminStatus } = useSesion();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const adminPayload = useMemo(() => ({
    tenantKey: String(tenantAdminDevice?.tenantKey ?? tenantAdminStatus?.tenantKey ?? "").trim().toUpperCase(),
    devicePublicId: String(tenantAdminDevice?.devicePublicId ?? tenantAdminStatus?.devicePublicId ?? "").trim().toUpperCase(),
    deviceKey: String(tenantAdminDevice?.deviceKey ?? "").trim().toUpperCase(),
    adminEmail: String(tenantAdminStatus?.adminEmail ?? tenantAdminDevice?.adminEmail ?? "").trim().toLowerCase(),
    appKey: "DASHBOARD",
    appVersion: process.env.REACT_APP_VERSION || "1.0.0",
  }), [tenantAdminDevice, tenantAdminStatus]);

  const canUseAdminDevice = !!(adminPayload.tenantKey && adminPayload.devicePublicId && adminPayload.deviceKey);
  const devices = data?.devices ?? data?.Devices ?? [];

  const getDevicePlatformText = (item) => String([
    item?.plataforma,
    item?.Plataforma,
    item?.fingerprintVersion,
    item?.FingerprintVersion,
    item?.nombreDispositivo,
    item?.NombreDispositivo,
  ].filter(Boolean).join(" "));
  const isMobileAccessDevice = (item) => /ANDROID|IPHONE|IPAD|IPOD|IOS|MOBILE|TABLET/i.test(getDevicePlatformText(item));
  const mobileDevices = devices.filter(isMobileAccessDevice);
  const workstationDevices = devices.filter((item) => !isMobileAccessDevice(item));
  const postAdmin = useCallback(async (url, extra = {}) => {
    const responseApi = await fetch(url, {
      method: "POST",
      body: JSON.stringify({ ...adminPayload, ...extra }),
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
    });

    const payload = await responseApi.json().catch(() => null);
    const result = payload?.result ?? payload;
    if (!responseApi.ok || payload?.hasError) {
      throw new Error(payload?.message ?? payload?.Message ?? result?.message ?? `Solicitud no procesada (${responseApi.status}).`);
    }
    return result;
  }, [adminPayload]);

  const loadDevices = useCallback(async () => {
    if (!canUseAdminDevice) return;
    setLoading(true);
    setError("");
    try {
      const result = await postAdmin(apiTenantMobileDeviceAccessList);
      setData(result);
    } catch (err) {
      setError(err?.message ?? "No fue posible cargar dispositivos.");
    } finally {
      setLoading(false);
    }
  }, [canUseAdminDevice, postAdmin]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const approveDevice = async (requestId) => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const result = await postAdmin(apiTenantMobileDeviceAccessApprove, { requestId });
      setMessage(result?.message ?? "Dispositivo aprobado.");
      await loadDevices();
    } catch (err) {
      setError(err?.message ?? "No fue posible aprobar el dispositivo.");
    } finally {
      setLoading(false);
    }
  };

  const revokeDevice = async (requestId) => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const result = await postAdmin(apiTenantMobileDeviceAccessRevoke, { requestId, motivo: "Revocado desde dashboard administrativo" });
      setMessage(result?.message ?? "Dispositivo revocado.");
      await loadDevices();
    } catch (err) {
      setError(err?.message ?? "No fue posible revocar el dispositivo.");
    } finally {
      setLoading(false);
    }
  };

  const deleteDevice = async (requestId) => {
    if (!window.confirm("Eliminar permanentemente este dispositivo? No se podrá recuperar.")) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const result = await postAdmin(apiTenantMobileDeviceAccessDelete, { requestId });
      setMessage(result?.message ?? "Dispositivo eliminado permanentemente.");
      await loadDevices();
    } catch (err) {
      setError(err?.message ?? "No fue posible eliminar el dispositivo.");
    } finally {
      setLoading(false);
    }
  };

  const renderDeviceTable = (title, subtitle, tableDevices, emptyMessage) => (
    <Stack spacing={1}>
      <Box>
        <Typography variant="h6" fontWeight={900}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Dispositivo</TableCell>
              <TableCell>Usuario</TableCell>
              <TableCell>Correo</TableCell>
              <TableCell>Estatus</TableCell>
              <TableCell>Solicitud</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableDevices.map((item) => {
              const status = statusLabels[item.estatus] ?? { label: item.estatus || "Sin estatus", color: "default" };
              const canApprove = item.estatus === "PENDING_APPROVAL";
              const canRevoke = item.estatus === "PENDING_APPROVAL" || item.estatus === "APPROVED";
              const canDelete = item.estatus !== "PENDING_APPROVAL";
              return (
                <TableRow key={item.requestId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={800}>{item.telefonoDispositivo || item.nombreDispositivo}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.plataforma}</Typography>
                  </TableCell>
                  <TableCell>{item.nombreUsuario}</TableCell>
                  <TableCell>{item.correo ?? item.Correo}</TableCell>
                  <TableCell><Chip size="small" label={status.label} color={status.color} /></TableCell>
                  <TableCell>{formatDate(item.requestedAtUtc)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {canApprove && (
                        <Button size="small" variant="contained" startIcon={<CheckCircleOutlineIcon />} onClick={() => approveDevice(item.requestId)} disabled={loading}>
                          Aprobar
                        </Button>
                      )}
                      {canRevoke && (
                        <Button size="small" variant="outlined" color="error" startIcon={<RemoveCircleOutlineIcon />} onClick={() => revokeDevice(item.requestId)} disabled={loading}>
                          Revocar
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="small" variant="text" color="error" startIcon={<DeleteForeverOutlinedIcon />} onClick={() => deleteDevice(item.requestId)} disabled={loading}>
                          Eliminar
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {!tableDevices.length && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "flex", flexDirection: "column", gap: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
        <Box>
          <Typography component="h1" variant="h5" fontWeight={900}>
            Administracion de dispositivos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Solicitudes pendientes, equipos aprobados y cupos disponibles de la licencia.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={loadDevices} disabled={loading || !canUseAdminDevice}>
            Actualizar
          </Button>
          <Button variant="text" onClick={() => navigate("/administraciontenant")}>
            Regresar a dashboard
          </Button>
        </Stack>
      </Stack>

      {!canUseAdminDevice && (
        <Alert severity="warning">
          Primero valida el acceso administrativo del tenant desde un equipo autorizado.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DevicesOutlinedIcon color="primary" />
            <Box>
              <Typography variant="caption" color="text.secondary">Activos</Typography>
              <Typography variant="h6" fontWeight={900}>{data?.activeDevices ?? 0} / {data?.maxDevices ?? 0}</Typography>
            </Box>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ScheduleOutlinedIcon color="warning" />
            <Box>
              <Typography variant="caption" color="text.secondary">Pendientes</Typography>
              <Typography variant="h6" fontWeight={900}>{data?.pendingDevices ?? 0}</Typography>
            </Box>
          </Stack>
        </Paper>
      </Stack>

      <Divider />

      {renderDeviceTable(
        "Dispositivos moviles",
        "Telefonos y tabletas pendientes o aprobados por el propietario.",
        mobileDevices,
        "No hay solicitudes moviles registradas."
      )}

      {renderDeviceTable(
        "Estaciones de trabajo",
        "PCs y equipos de escritorio que solicitan acceso a una licencia ya activada.",
        workstationDevices,
        "No hay solicitudes de estaciones de trabajo registradas."
      )}
    </Box>
  );
}
